import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';

import { AppDispatch } from 'store';
import { setMapMode } from 'store/mapSlice';
import { RootState } from 'store/rootReducer';
import {
  clearSearchBebyggelseobjektResult,
  searchBebyggelseobjekt,
  setSearchCriteria
} from 'store/searchBebyggelseobjektSlice';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';
import { useDrawerMobileTemplate } from 'hooks/useDrawerMobileTemplate';
import { useInitialFocusOnExtent } from 'hooks/useInitialFocusOnExtent';

import { RaaSpinner } from 'raa-react';

import { DrawerBody, DrawerContainer, NavigationDrawerHead } from 'components/drawerSections/DrawerSections';
import { activeSource, replaceBebyggelseobjektResultInActiveSource } from 'components/map/Layers';
import Pagination from 'components/search/Pagination';
import BebyggelseobjektSearchCriterias from 'components/search/bebyggelseobjekt/BebyggelseobjektSearchCriterias';
import { getStringArray } from 'components/search/common';

import { SearchBebyggelseobjektCriteria, SearchBebyggelseobjektPost, SearchResultBebyggelseobjekt } from 'models';

import { MapModeEnum } from 'enums';

import { useSearchLmFastighetQuery } from 'api/endpoints/LMSearchFastighetSlice';
import { getIconBySkydd } from 'utilities/bebyggelseobjektUtilities';
import { isFetchBaseQueryModelError } from 'utilities/typeGuards';

const BebyggelseobjektSearchResult: FC = () => {
  const dispatch = useAppDispatch();
  const hasSearchedRef = useRef(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState<number>(
    searchParams.get('page') ? Number.parseInt(searchParams.get('page')!) : 0
  );
  const [criteria, setCritera] = useState<SearchBebyggelseobjektCriteria>();
  const [showSpinner, setShowSpinner] = useState(false);
  const [isGeometryMissing, setIsGeometryMissing] = useState(false);

  const { requestStatus, data: bebyggelseobjektResult } = useAppSelector(
    (state: RootState) => state.searchBebyggelseobjekt
  );

  const { setDrawerMobileTemplate } = useDrawerMobileTemplate();

  const beteckning = searchParams.get('beteckning') ?? undefined;

  const {
    data: searchLmFastighetData,
    isSuccess: isSuccessLmFastighet,
    isError: isErrorLmFastighet,
    isFetching: isFetchingLmFastighet,
    error: errorFetchLmFastighet
  } = useSearchLmFastighetQuery(beteckning, {
    skip: !beteckning
  });

  const pageSize = 50;

  const clearResult = (dispatch: AppDispatch) => {
    activeSource.clear();
    activeSource.changed();
    dispatch(clearSearchBebyggelseobjektResult());
  };

  useEffect(() => {
    if (isErrorLmFastighet) {
      console.error('Fel vid hämtning av fastighet', errorFetchLmFastighet);
    }
  }, [isErrorLmFastighet, errorFetchLmFastighet]);

  useEffect(() => {
    dispatch(setMapMode(MapModeEnum.SEARCH));

    // Cleanup
    return () => {
      dispatch(setMapMode(MapModeEnum.DEFAULT));
      setDrawerMobileTemplate(undefined);
    };
  }, [dispatch, setDrawerMobileTemplate]);

  useEffect(() => {
    if (hasSearchedRef.current) return;

    if (searchParams.has('beteckning') && !isSuccessLmFastighet) return;

    const hasSearchParams =
      searchParams.has('skyddstyp') ||
      searchParams.has('bebyggelsenummer') ||
      searchParams.has('namn') ||
      searchParams.has('kommun_kod') ||
      searchParams.has('beteckning') ||
      searchParams.has('lan_kod');

    const criteriaBase: SearchBebyggelseobjektCriteria = hasSearchParams
      ? {
          skyddstyper: getStringArray(searchParams.get('skyddstyp')),
          bebyggelsenummer: getStringArray(searchParams.get('bebyggelsenummer')),
          namn: searchParams.get('namn') || undefined,
          kommun_kod: getStringArray(searchParams.get('kommun_kod')),
          lan_kod: getStringArray(searchParams.get('lan_kod')),
          beror_geometri: searchLmFastighetData?.geometri || undefined,
          beteckning: searchParams.get('beteckning') || undefined
        }
      : {
          skyddstyper: [],
          bebyggelsenummer: [],
          namn: '',
          kommun_kod: [],
          lan_kod: [],
          beror_geometri: undefined,
          beteckning: ''
        };

    setCritera(criteriaBase);
    hasSearchedRef.current = true;
  }, [searchParams, isSuccessLmFastighet, searchLmFastighetData]);

  useEffect(() => {
    if (criteria) {
      const { beteckning: _, ...criteriaWithoutBeteckning } = criteria;
      dispatch(setSearchCriteria(criteria));

      if (searchLmFastighetData && !criteria.beror_geometri) {
        setIsGeometryMissing(true);
        console.error('Vald fastighet saknade geometri. Sökning ignoreras.');
        return;
      }

      const post: SearchBebyggelseobjektPost = {
        criteria: criteriaWithoutBeteckning,
        page: currentPage,
        pagesize: pageSize
      };
      dispatch(searchBebyggelseobjekt(post));
    }

    return () => clearResult(dispatch);
  }, [criteria, dispatch, currentPage, searchLmFastighetData]);

  useEffect(() => {
    setSearchParams(
      (params) => {
        params.set('page', currentPage.toString());
        return params;
      },
      { replace: true }
    );
  }, [currentPage, setSearchParams]);

  useInitialFocusOnExtent(
    useCallback(() => {
      if (bebyggelseobjektResult?.results && bebyggelseobjektResult?.results.length > 0) {
        replaceBebyggelseobjektResultInActiveSource(bebyggelseobjektResult?.results as SearchResultBebyggelseobjekt[]);
      } else {
        clearResult(dispatch);
      }
    }, [bebyggelseobjektResult?.results, dispatch])
  );

  useEffect(() => {
    if (requestStatus === 'pending' || isFetchingLmFastighet) {
      setShowSpinner(true);
    } else {
      const timeout = setTimeout(() => {
        setShowSpinner(false);
      }, 150);

      return () => clearTimeout(timeout);
    }
  }, [requestStatus, isFetchingLmFastighet]);

  useEffect(() => {
    if (bebyggelseobjektResult && bebyggelseobjektResult.total_size > 0) {
      setDrawerMobileTemplate(
        <div className="tw-flex tw-flex-col tw-gap-2 tw-p-4">
          <h1 className="tw-heading-md tw-w-64 tw-truncate md:tw-w-full">Sökresultat</h1>
          <div className="tw-flex tw-items-center tw-justify-start">
            <p className="tw-mb-2">{bebyggelseobjektResult?.total_size} bebyggelseobjekt</p>
          </div>
        </div>
      );
    }
  }, [bebyggelseobjektResult, setDrawerMobileTemplate]);

  return (
    <DrawerContainer>
      <NavigationDrawerHead />
      {showSpinner ? (
        <>
          <div className="tw-p-4 md:tw-p-6" />
          <DrawerBody>
            <div className="tw-p-4 md:tw-p-6">
              <div className="tw-flex tw-justify-center tw-py-8">
                <RaaSpinner size="3rem" />
              </div>
            </div>
          </DrawerBody>
        </>
      ) : (
        <>
          <div className="tw-border-b tw-border-raa-gray-3 tw-p-4 md:tw-p-6">
            <h1 className="tw-heading-md tw-mb-2">{bebyggelseobjektResult?.total_size} bebyggelseobjekt</h1>
            <BebyggelseobjektSearchCriterias />
          </div>
          <DrawerBody>
            {isErrorLmFastighet && isFetchBaseQueryModelError(errorFetchLmFastighet) && (
              <div className="tw-p-8 tw-text-raa-error-red">
                <p>Något gick fel vid hämtning av fastighetsdata</p>
                <p>
                  Meddelande: <span className="tw-italic">{errorFetchLmFastighet.data.message}</span>
                </p>
              </div>
            )}

            {isGeometryMissing && (
              <div className="tw-p-8 tw-text-raa-error-red">
                <p>Fastigheten saknar yta och kan därför inte användas för sökning.</p>
              </div>
            )}

            <ul>
              {bebyggelseobjektResult?.results &&
                bebyggelseobjektResult.results.map((item: SearchResultBebyggelseobjekt) => {
                  return (
                    <li key={item.id} className="tw-cursor-pointer tw-border-b tw-border-raa-gray-2">
                      <NavLink
                        className="tw-block tw-overflow-auto tw-px-4 tw-py-2 focus:tw-outline-transparent md:tw-px-4 md:tw-py-4"
                        to={`/bebyggelseobjekt/${item.id}`}
                      >
                        <div
                          className="tw-row-gap-2 tw-col-gap-4 tw-grid tw-min-w-full"
                          style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))' }}
                        >
                          <div className="tw-flex tw-justify-between">
                            <div className="tw-flex tw-flex-col tw-pt-2">
                              <span aria-hidden="true">
                                <img
                                  src={`data:image/svg+xml;utf8,${encodeURIComponent(
                                    getIconBySkydd(item.skyddstyp, item.havt_skydd)
                                  )}`}
                                  alt="Ikon för publicerat bebyggelseobjekt"
                                  className="tw-mr-2 tw-h-6"
                                />
                              </span>
                            </div>
                            <div className="tw-ml-2 tw-mr-4 tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden">
                              <span className="tw-text-size-1 tw-font-semibold tw-text-raa-orange">
                                {item.titel} <span className="tw-italic">({item.bebyggelsenummer})</span>
                              </span>
                              <span className="tw-truncate tw-pr-3 tw-text-size-0.813">
                                {item.skyddstyp_namn}
                                {item.havt_skydd && ' (HÄVT)'}
                              </span>
                            </div>
                          </div>
                          <div className="tw-flex tw-justify-between">
                            <div className="tw-flex tw-flex-col tw-overflow-hidden tw-text-size-0.813">
                              <span className="tw-truncate">
                                {item.geografisk_indelning?.kommun?.map((k) => k.kommun_namn).join(', ')}
                              </span>
                              <span className="tw-truncate">
                                {item.geografisk_indelning?.kommun?.map((k) => `${k.lan_namn}s län`).join(', ')}
                              </span>
                            </div>
                            <div className="tw-flex tw-flex-1 tw-justify-end tw-text-size-0.813">
                              <div className="tw-flex tw-flex-col tw-text-right">
                                <span className="tw-truncate">{item.publiceringsdatum}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </NavLink>
                    </li>
                  );
                })}
            </ul>
          </DrawerBody>
        </>
      )}

      {bebyggelseobjektResult && bebyggelseobjektResult.total_size >= pageSize && (
        <Pagination results={bebyggelseobjektResult} setCurrentPage={setCurrentPage} requestStatus={requestStatus} />
      )}
    </DrawerContainer>
  );
};

export default BebyggelseobjektSearchResult;
